import { useMemo } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  collection,
  query,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Quote, QuoteInput, CostEstimate, Deal } from '@elka/types';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import {
  calculateQuoteItemSum,
  calculateQuoteSubtotal,
  calculateQuoteTotal,
  applyDiscounts,
} from '@elka/utils';
import { VAT_RATE, assertDocWritable, isDocLocked } from '@elka/types';
import { updateDealQuotingStatus, type DealQuotingSyncResult } from '@/lib/updateDealQuotingStatus';
import { allocateSequentialNumber } from '@/lib/allocateSequentialNumber';
import { useCostEstimates } from '../../cost-estimates/hooks/useCostEstimates';
import { applyRestoredClientPrices } from '../lib/applyRestoredClientPrices';

// Overrides used when cloning a quote into a NEW deal (deal duplication), instead of
// re-snapshotting the source quote's own (unrelated) deal.
export interface CloneQuoteOverrides {
  dealId: string;
  costEstimateId: string;
  companyId: string;
  companyName: string;
  contactId?: string;
  contactName?: string;
  projectName?: string;
}

export function useQuotes() {
  const { user } = useAuth();
  // Non-subscribing instance — only for the cost-estimate write-back below (see
  // restoreDraftFromQuote). subscribe:false means no extra Firestore listener.
  const { updateCostEstimate } = useCostEstimates({ subscribe: false });

  const quotesQuery = useMemo(
    () =>
      query(collection(db, ...splitPath(COLLECTIONS.quotes.quotes)), orderBy('createdAt', 'desc')),
    []
  );

  const [snapshot, loading, error] = useCollection(quotesQuery);

  const quotes =
    (snapshot?.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Quote[]) || [];

  const generateQuoteNumber = () =>
    allocateSequentialNumber([...splitPath(COLLECTIONS.quotes.counters), 'quotes'], 'ה');

  const addQuote = async (data: QuoteInput): Promise<Quote> => {
    if (!user) throw new Error('User not authenticated');

    const itemsWithSums = data.items.map((item) => {
      const clean: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(item)) {
        if (v !== undefined) clean[k] = v;
      }
      clean.sum = calculateQuoteItemSum(item);
      return clean;
    });

    const itemsSubtotal = calculateQuoteSubtotal(itemsWithSums as unknown as typeof data.items);
    const discountAmount = data.discountAmount || 0;
    const subtotal = Math.max(0, itemsSubtotal - discountAmount);
    const { taxAmount, total } = calculateQuoteTotal(subtotal, data.taxRate || VAT_RATE);

    const quoteNumber = await generateQuoteNumber();

    // Strip undefined values — Firestore rejects them
    const cleanData: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined) cleanData[k] = v;
    }

    const savedData = {
      ...cleanData,
      quoteNumber,
      items: itemsWithSums,
      subtotal,
      taxAmount,
      total,
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.quotes.quotes)), savedData);

    // Write quoteId + status:'quoted' back to the linked cost estimate so the workspace
    // and status derivation see the quote immediately after creation.
    if (data.costEstimateId) {
      updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), data.costEstimateId), {
        quoteId: docRef.id,
        status: 'quoted',
        updatedBy: user.displayName || user.email || 'Unknown',
        updatedAt: serverTimestamp(),
      }).catch((err) => console.error('[addQuote] estimate writeback failed:', err));
    }

    // Update deal quoting status (fire-and-forget — don't block the caller)
    if (data.dealId) {
      updateDealQuotingStatus(data.dealId);
    }

    return { ...savedData, id: docRef.id } as unknown as Quote;
  };

  /**
   * Returns the (still-running) deal-status sync as `dealSync` when one was started, so a
   * caller that cares — e.g. marking a quote 'accepted', which can auto-advance the deal to
   * 'won' — can await it and react. Everyone else can keep ignoring the return value: the
   * sync stays fire-and-forget and never rejects.
   */
  const updateQuote = async (
    id: string,
    data: Partial<QuoteInput>
  ): Promise<{ dealSync?: Promise<DealQuotingSyncResult> }> => {
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = { ...data };
    const existing = quotes.find((q) => q.id === id);

    // Once a quote leaves 'draft' it is what the client was sent — content is frozen.
    assertDocWritable('quote', existing?.status, data);

    if (data.items) {
      const itemsWithSums = data.items.map((item) => ({
        ...item,
        sum: calculateQuoteItemSum(item),
      }));

      const itemsSubtotal = calculateQuoteSubtotal(itemsWithSums);

      // Resolve the effective discount: prefer values on `data`, else fall back to the
      // existing quote — otherwise an items-only save (estimate-sync effect, single-line
      // edits) silently drops the stored discount and overcharges the client.
      const discountType = data.discountType ?? existing?.discountType;
      const discountValue = data.discountValue ?? existing?.discountValue;
      const extraDiscounts = data.extraDiscounts ?? existing?.extraDiscounts;
      // Recompute against the NEW subtotal (see BillPage); with no discount spec at all,
      // reuse the resolved discountAmount.
      const hasDiscountSpec =
        discountType !== undefined || discountValue !== undefined || !!extraDiscounts?.length;
      const discountAmount = hasDiscountSpec
        ? applyDiscounts(itemsSubtotal, { discountType, discountValue, extraDiscounts }).total
        : (data.discountAmount ?? existing?.discountAmount ?? 0);

      const subtotal = Math.max(0, itemsSubtotal - discountAmount);
      const { taxAmount, total } = calculateQuoteTotal(subtotal, data.taxRate || VAT_RATE);

      updateData.items = itemsWithSums;
      updateData.subtotal = subtotal;
      updateData.taxAmount = taxAmount;
      updateData.total = total;
      // Persist the resolved discount so it doesn't go stale on an items-only save.
      if (discountType !== undefined) updateData.discountType = discountType;
      if (discountValue !== undefined) updateData.discountValue = discountValue;
      if (extraDiscounts !== undefined) updateData.extraDiscounts = extraDiscounts;
      updateData.discountAmount = discountAmount;
    } else if (
      data.discountAmount !== undefined ||
      data.discountValue !== undefined ||
      data.extraDiscounts !== undefined
    ) {
      // Discount changed without items — recalculate totals from existing quote items
      const sourceItems = existing?.items;
      if (sourceItems) {
        const itemsSubtotal = calculateQuoteSubtotal(sourceItems);
        const discountAmount = (data.discountAmount as number) ?? existing?.discountAmount ?? 0;
        const subtotal = Math.max(0, itemsSubtotal - discountAmount);
        const { taxAmount, total } = calculateQuoteTotal(subtotal, data.taxRate || VAT_RATE);
        updateData.subtotal = subtotal;
        updateData.taxAmount = taxAmount;
        updateData.total = total;
      }
    }

    // Auto-set status timestamps so they're always in sync with status changes
    if (data.status === 'sent' && !updateData.sentAt) {
      updateData.sentAt = serverTimestamp();
    } else if (data.status === 'accepted' && !updateData.acceptedAt) {
      updateData.acceptedAt = serverTimestamp();
    } else if (data.status === 'rejected' && !updateData.rejectedAt) {
      updateData.rejectedAt = serverTimestamp();
    }

    updateData.updatedBy = user.displayName || user.email || 'Unknown';
    updateData.updatedAt = serverTimestamp();

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.quotes), id), updateData);

    // Update deal quoting status (fire-and-forget — don't block the caller).
    // Sync the new dealId when one is being assigned for the first time
    // (e.g. converting a quick quote into a deal-linked quote). Skip the sync for
    // presentation-only edits (e.g. the PDF sub-chapter-summary toggle): the
    // derived quoting status only depends on status/items/dealId/discount-driven
    // totals, and re-running it would needlessly bump the deal's updatedAt
    // (reordering the "last activity" deals list) on a cosmetic change.
    const dealIdToSync = (data.dealId as string | undefined) || existing?.dealId;
    const affectsQuotingStatus =
      data.status !== undefined ||
      data.items !== undefined ||
      data.dealId !== undefined ||
      data.discountAmount !== undefined ||
      data.discountValue !== undefined ||
      data.extraDiscounts !== undefined ||
      data.taxRate !== undefined;
    const dealSync =
      dealIdToSync && affectsQuotingStatus ? updateDealQuotingStatus(dealIdToSync) : undefined;

    return { dealSync };
  };

  const deleteQuote = async (id: string) => {
    const existing = quotes.find((q) => q.id === id);
    if (isDocLocked(existing?.status)) {
      throw new Error('ההצעה נעולה ולא ניתנת למחיקה');
    }
    const dealId = existing?.dealId;

    await deleteDoc(doc(db, ...splitPath(COLLECTIONS.quotes.quotes), id));

    // Update deal quoting status. The quote is already deleted at this point, so a
    // status-sync failure must not reject the whole operation (which would surface a
    // misleading "delete failed" to the caller). Best-effort + log.
    if (dealId) {
      await updateDealQuotingStatus(dealId).catch((err) =>
        console.error('[deleteQuote] deal status sync after delete failed:', err)
      );
    }
  };

  // Lock a draft quote as a historical version. The quote doc already holds a
  // full snapshot (items, totals, margin, labor) — the existing QuotePdf
  // component renders from that data, so we don't persist a PDF file here.
  const finalizeQuote = async (id: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    const existing = quotes.find((q) => q.id === id);
    if (!existing) throw new Error('Quote not found');
    if (existing.status !== 'draft') {
      throw new Error('Only draft quotes can be finalized');
    }

    await updateDoc(doc(db, ...splitPath(COLLECTIONS.quotes.quotes), id), {
      status: 'finalized',
      finalizedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: user.displayName || user.email || 'Unknown',
    });

    if (existing.dealId) {
      updateDealQuotingStatus(existing.dealId);
    }
  };

  // Clone a finalized (or any) quote into a new draft. Copies items, totals,
  // notes, terms, discount, margin — the user tweaks pricing from there. The
  // new draft gets its own auto-incremented quote number and fresh timestamps.
  const cloneQuoteAsDraft = async (
    source: string | Quote,
    overrides?: CloneQuoteOverrides
  ): Promise<Quote> => {
    if (!user) throw new Error('User not authenticated');
    const sourceQuote = typeof source === 'string' ? quotes.find((q) => q.id === source) : source;
    if (!sourceQuote) throw new Error('Source quote not found');

    const quoteNumber = await generateQuoteNumber();
    const now = new Date().toISOString().slice(0, 10);

    // Client/contact are a snapshot of the deal at creation time. A new version
    // must re-snapshot from the CURRENT deal — otherwise a client/contact fixed
    // in CRM can never reach any quote PDF (old versions are frozen by design).
    // Skipped when `overrides` is given (deal duplication): the new deal's client
    // is the caller's target company, not the source quote's own deal.
    let liveDeal: Deal | null = null;
    if (!overrides && sourceQuote.dealId) {
      try {
        const dealSnap = await getDoc(
          doc(db, ...splitPath(COLLECTIONS.crm.deals), sourceQuote.dealId)
        );
        if (dealSnap.exists()) liveDeal = { id: dealSnap.id, ...dealSnap.data() } as Deal;
      } catch (err) {
        console.error('[cloneQuoteAsDraft] deal fetch failed, keeping source client:', err);
      }
    }

    // Rebuild the payload from scratch — don't spread the source because we
    // must drop all identity/audit fields (id, timestamps, pdfUrl, status).
    const savedData: Record<string, unknown> = {
      quoteNumber,
      quoteType: sourceQuote.quoteType,
      companyId: overrides ? overrides.companyId : (liveDeal?.companyId ?? sourceQuote.companyId),
      companyName: overrides
        ? overrides.companyName
        : (liveDeal?.companyName ?? sourceQuote.companyName),
      items: sourceQuote.items,
      subtotal: sourceQuote.subtotal,
      taxRate: sourceQuote.taxRate,
      taxAmount: sourceQuote.taxAmount,
      total: sourceQuote.total,
      validUntil: sourceQuote.validUntil,
      status: 'draft',
      quoteDate: now,
      materialsCost: sourceQuote.materialsCost,
      laborDays: sourceQuote.laborDays,
      dayRate: sourceQuote.dayRate,
      laborCost: sourceQuote.laborCost,
      totalCost: sourceQuote.totalCost,
      marginPercent: sourceQuote.marginPercent,
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: serverTimestamp(),
    };
    // Optional fields — only include when set on the source, to avoid writing
    // undefined (Firestore rejects it).
    const maybeCopy = <K extends keyof Quote>(key: K) => {
      if (sourceQuote[key] !== undefined && sourceQuote[key] !== null) {
        savedData[key as string] = sourceQuote[key];
      }
    };
    if (overrides) {
      // Deal duplication: identity fields come from the caller's new deal/estimate,
      // never from the source quote (which belongs to a different deal entirely).
      savedData.dealId = overrides.dealId;
      savedData.costEstimateId = overrides.costEstimateId;
      savedData.clientId = overrides.companyId;
      if (overrides.contactId !== undefined) savedData.contactId = overrides.contactId;
      if (overrides.contactName !== undefined) savedData.contactName = overrides.contactName;
      if (overrides.projectName !== undefined) savedData.projectName = overrides.projectName;
      // projectId intentionally dropped — the new deal has no elka-manager project.
    } else if (liveDeal) {
      // Deal found — its client/contact win over the source snapshot, including
      // clearing a contact that was removed from the deal.
      if (liveDeal.contactId) savedData.contactId = liveDeal.contactId;
      if (liveDeal.contactName) savedData.contactName = liveDeal.contactName;
      savedData.clientId = liveDeal.companyId;
    } else {
      maybeCopy('contactId');
      maybeCopy('contactName');
      maybeCopy('clientId');
    }
    if (!overrides) {
      maybeCopy('dealId');
      maybeCopy('projectId');
      maybeCopy('projectName');
      maybeCopy('costEstimateId');
    }
    maybeCopy('notes');
    maybeCopy('terms');
    maybeCopy('equipmentCosts');
    maybeCopy('equipmentTotalCost');
    maybeCopy('targetPrice');
    maybeCopy('discountType');
    maybeCopy('discountValue');
    maybeCopy('discountAmount');
    maybeCopy('extraDiscounts');
    maybeCopy('pdfShowSubChapterSummary');
    maybeCopy('pdfShowBsd');

    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.quotes.quotes)), savedData);

    const dealIdToSync = overrides ? overrides.dealId : sourceQuote.dealId;
    if (dealIdToSync) {
      updateDealQuotingStatus(dealIdToSync);
    }
    return { ...savedData, id: docRef.id } as unknown as Quote;
  };

  // For boq-with-prices the client-facing line prices are owned by the COST ESTIMATE
  // (item.clientPrice), not the quote: the תמחור editor re-derives every quote line from
  // the estimate on load (buildSyncedQuoteItems). Restoring only the draft quote is
  // therefore silently reverted — and overwritten — the moment the user opens "ערוך הצעה".
  // Push the restored version's per-line prices back onto the estimate so the restore
  // actually sticks through the editor. clientPrice doesn't feed the cost aggregates, so
  // updateCostEstimate's recompute leaves materials/total/target intact.
  const restoreEstimateClientPrices = async (source: Quote) => {
    const estimateId = source.costEstimateId;
    if (!estimateId) return;
    const snap = await getDoc(doc(db, ...splitPath(COLLECTIONS.quotes.costEstimates), estimateId));
    if (!snap.exists()) return;
    const estimate = { id: snap.id, ...snap.data() } as CostEstimate;

    const { items: nextItems, changed } = applyRestoredClientPrices(estimate.items, source.items);
    if (!changed) return;
    await updateCostEstimate(estimateId, { items: nextItems });
  };

  // Restore the working draft from a locked (sent/accepted/finalized) bid: overwrite the
  // draft's content with the source bid's pricing/content, keeping the draft's identity
  // (id, quoteNumber, status='draft'). If no draft exists yet for the deal, a fresh draft
  // is cloned from the source instead so the user always lands on an editable draft.
  // Returns the id of the draft that now holds the restored content.
  const restoreDraftFromQuote = async (sourceId: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    const source = quotes.find((q) => q.id === sourceId);
    if (!source) throw new Error('Source quote not found');

    // boq-with-prices: the editor reads client prices from the estimate, so restore them
    // there too — otherwise the next sync wipes out the restored draft. Do this first so a
    // failure aborts before we touch the draft (no half-restore).
    // mashkal is intentionally excluded: its quote unitPrice is the list price after chapter
    // discounts, so writing it back onto item.clientPrice would overwrite the list base price
    // and compound on the next sync. Restoring a mashkal version restores items/discount only;
    // listDiscountByChapter on the estimate stays authoritative.
    if (source.quoteType === 'boq-with-prices') {
      await restoreEstimateClientPrices(source);
    }

    // Newest existing draft for the same deal (quotes are ordered createdAt desc).
    const draft = quotes.find((q) => q.dealId === source.dealId && q.status === 'draft');
    if (!draft) {
      const created = await cloneQuoteAsDraft(sourceId);
      return created.id;
    }

    // Overwrite content only — updateQuote recomputes subtotal/taxAmount/total from
    // items + discountAmount. Drop undefined so we never write undefined to Firestore.
    const content: Partial<QuoteInput> = {
      items: source.items,
      taxRate: source.taxRate,
      validUntil: source.validUntil,
      notes: source.notes,
      terms: source.terms,
      marginPercent: source.marginPercent,
      materialsCost: source.materialsCost,
      laborDays: source.laborDays,
      dayRate: source.dayRate,
      laborCost: source.laborCost,
      totalCost: source.totalCost,
      equipmentCosts: source.equipmentCosts,
      equipmentTotalCost: source.equipmentTotalCost,
      targetPrice: source.targetPrice,
      discountType: source.discountType,
      discountValue: source.discountValue,
      discountAmount: source.discountAmount,
      extraDiscounts: source.extraDiscounts ?? [],
    };
    const clean: Partial<QuoteInput> = {};
    for (const [k, v] of Object.entries(content)) {
      if (v !== undefined) (clean as Record<string, unknown>)[k] = v;
    }
    await updateQuote(draft.id, clean);
    return draft.id;
  };

  return {
    quotes,
    loading,
    error,
    addQuote,
    updateQuote,
    deleteQuote,
    finalizeQuote,
    cloneQuoteAsDraft,
    restoreDraftFromQuote,
  };
}
